import { ReactNode } from 'react';
import { FieldPath, FieldValues, UseControllerProps, useController } from 'react-hook-form';
import { DateTimePicker, DateTimePickerProps } from '@givebutter/primitives/date-time-picker';
import { get } from 'lodash';
import { DateTime } from 'luxon';
import styled from 'styled-components';

interface UseFormDateTimePickerProps<T extends FieldValues, K extends FieldPath<T>>
    extends UseControllerProps<T, K>,
        DateTimePickerProps {
    children?: ReactNode;
}

const UseFormDateTimePicker = <T extends FieldValues, K extends FieldPath<T>>(
    props: UseFormDateTimePickerProps<T, K>,
) => {
    const {
        control,
        name,
        defaultValue,
        rules,
        onChange: onChangeConsumer,
        startDateTimeProps,
        endDateTimeProps,
        children = null,
        ...restDateTimePickerProps
    } = props;
    const {
        field: {
            ref: _,
            onChange: onChangeInternal,
            onBlur: onBlurInternal,
            value,
            ...restFormProps
        },
        formState: { errors },
    } = useController({
        name,
        control,
        defaultValue,
        rules,
    });

    const error = get(errors, name);

    const onChange = (value: [DateTime | undefined, DateTime | undefined]) => {
        onChangeInternal(value);
        onChangeConsumer?.(value);
    };

    return (
        <Wrapper>
            <DateTimePicker
                {...restFormProps}
                {...restDateTimePickerProps}
                isError={!!error?.message}
                startDateTimeProps={{
                    ...startDateTimeProps,
                    startInputProps: {
                        ...startDateTimeProps?.startInputProps,
                    },
                }}
                endDateTimeProps={{
                    ...endDateTimeProps,
                    startInputProps: {
                        ...endDateTimeProps?.startInputProps,
                    },
                }}
                value={value as [DateTime, DateTime]}
                onChange={onChange}
            />
            {children}
        </Wrapper>
    );
};

const Wrapper = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    row-gap: 0.25rem;
    align-items: flex-start;
`;

export { UseFormDateTimePicker };
export type { UseFormDateTimePickerProps };
